import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export interface StoryStudioSessionDto {
  id: string;
  projectId: string;
  synopsis?: string | null;
  worldBuildingNotes?: string | null;
  mood?: string | null;
  pace?: string | null;
  narrativeVoice?: string | null;
  linkedEntityIds: string[];
  state?: unknown;
  updatedAt: string;
}

export interface TitleSuggestionDto {
  title: string;
  rationale: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export function useStoryStudioSession(projectId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<StoryStudioSessionDto | null>({
    enabled: !!projectId,
    queryKey: ['story-session', projectId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/story-sessions/by-project/${projectId}`)
        .json<ApiEnvelope<StoryStudioSessionDto | null>>();
      return resp.data;
    },
  });
}

export function useUpsertStoryStudio(projectId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<StoryStudioSessionDto>) => {
      const client = createApiClient(getToken);
      const resp = await client
        .put(`api/v2/story-sessions/by-project/${projectId}`, { json: input })
        .json<ApiEnvelope<StoryStudioSessionDto>>();
      return resp.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['story-session', projectId] }),
  });
}

export function useGenerateStoryBrief(projectId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/story-sessions/by-project/${projectId}/generate-brief`)
        .json<ApiEnvelope<unknown>>();
      return resp.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['story-session', projectId] }),
  });
}

export function useSuggestBookTitles(projectId: string) {
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/story-sessions/by-project/${projectId}/suggest-titles`)
        .json<ApiEnvelope<TitleSuggestionDto[]>>();
      return resp.data ?? [];
    },
  });
}

// --- Session by ID (standalone or book-linked) ---

export function useStoryStudioSessionById(sessionId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<StoryStudioSessionDto | null>({
    enabled: !!sessionId,
    queryKey: ['story-session-by-id', sessionId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/story-sessions/${sessionId}`)
        .json<ApiEnvelope<StoryStudioSessionDto | null>>();
      return resp.data;
    },
  });
}

export function useUpsertStoryStudioById(sessionId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<StoryStudioSessionDto>) => {
      const client = createApiClient(getToken);
      const resp = await client
        .put(`api/v2/story-sessions/${sessionId}`, { json: input })
        .json<ApiEnvelope<StoryStudioSessionDto>>();
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['story-session-by-id', sessionId] });
      qc.invalidateQueries({ queryKey: ['story-sessions-all'] });
    },
  });
}

export function useDeleteStoryStudioSession() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/story-sessions/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['story-sessions-all'] });
    },
  });
}

// --- Standalone listing ---

export interface StoryStudioSessionSummaryDto {
  id: string;
  projectId?: string | null;
  projectTitle?: string | null;
  synopsis?: string | null;
  updatedAt: string;
}

export function useMyStoryStudioSessions() {
  const { getToken } = useAuth();
  return useQuery<StoryStudioSessionSummaryDto[]>({
    queryKey: ['story-sessions-all'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get('api/v2/story-sessions')
        .json<ApiEnvelope<StoryStudioSessionSummaryDto[]>>();
      return resp.data ?? [];
    },
  });
}

export function useDictionarySearch(query: string, bookId?: string) {
  const { getToken } = useAuth();
  return useQuery<
    Array<{
      kind: string;
      id: string;
      name: string;
      description?: string | null;
    }>
  >({
    enabled: query.length > 1,
    queryKey: ['dictionary', query, bookId ?? ''],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const params = new URLSearchParams({ q: query });
      if (bookId) params.set('book_id', bookId);
      const resp = await client
        .get(`api/v2/dictionary?${params.toString()}`)
        .json<
          ApiEnvelope<
            Array<{
              kind: string;
              id: string;
              name: string;
              description?: string | null;
            }>
          >
        >();
      return resp.data ?? [];
    },
  });
}
